'use client'

import React, { useState } from 'react';
import { StyledDiv, FaqItem, FaqButton, FaqContent } from './faq.styles';
import { CSSProperties } from 'styled-components';

interface IFaqProps {
    faqs: { q: string, a: string; }[];
    mode?: 'light' | 'dark';
    style?: CSSProperties;
}

export const Faq = ({ faqs = [], mode = 'light', style }: IFaqProps) => {
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    if (!faqs.length) return <></>;

    const toggleFaq = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <>
            <FaqScripts faqs={faqs} />
            <StyledDiv style={style} className={mode}>
                <h3>FAQ</h3>
                <div>
                    {faqs.map((faq, index) => (
                        <FaqItem key={index} className={mode}>
                            <FaqButton
                                onClick={() => toggleFaq(index)}
                                aria-expanded={openIndex === index}
                                aria-controls={`faq-content-${index}`}
                                id={`faq-button-${index}`}
                                className={mode}
                            >
                                <span dangerouslySetInnerHTML={{ __html: faq.q }} />
                                <svg
                                    width="30"
                                    height="30"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    style={{
                                        transform: openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.3s ease',
                                    }}
                                >
                                    <path
                                        d="M7 10l5 5 5-5"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </FaqButton>
                            <FaqContent
                                id={`faq-content-${index}`}
                                role="region"
                                aria-labelledby={`faq-button-${index}`}
                                className={openIndex === index ? 'open' : ''}
                            >
                                <div dangerouslySetInnerHTML={{ __html: faq.a }} />
                            </FaqContent>
                        </FaqItem>
                    ))}
                </div>
            </StyledDiv>
        </>
    );
};


const FaqScripts = ({ faqs }: { faqs: { q: string, a: string; }[]; }) => {
    function extractContent(str: string) {
        if (typeof document === 'undefined') {
            // Server-side: use regex to strip HTML tags
            return str.replace(/<[^>]*>/g, '').replace(/\n/g, ' ').trim();
        }
        const div = document.createElement('div');
        div.innerHTML = str;
        return (div.textContent || div.innerText).replace(/\n/g, ' ').trim();
    };

    const schema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map((item) => ({
            "@type": "Question",
            "name": extractContent(item.q),
            "acceptedAnswer": {
                "@type": "Answer",
                "text": extractContent(item.a.replace(/"/g, "'"))
            }
        }))
    };

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
    );
};